import ko from "knockout";
import "flow/utils/dateHelper";
import { MaybeObservable, MaybeObservableAttributes, ObservableAttributes } from "flow/interfaces/MaybeObservable";
import viewHtml from "./date.html";
import createEnabler from "flow/koHelpers/createEnablerDisabler";
import { interceptAttributesWithDefaultsIgnoreId } from "flow/koHelpers/interceptAttributes";
import { selectIdStatic } from "flow/koHelpers/selectId";
import { selectLabel, selectPlaceholder } from "flow/koHelpers/selectParam";
import createRequiredObservable from "flow/koHelpers/createRequiredObservable";
import { Dictionary } from "flow/api/types";
import metaCache from "flow/utils/metaCache";

type DatePickableJQueryElement = JQuery<Element> & IDatePickerControl;

interface IDatePickerControl {
    datepicker(options: JQueryUI.DatepickerOptions): DatePickableJQueryElement;
    datepicker(operation: "show" | "destroy"): DatePickableJQueryElement;
}

export interface IDateParams {
    value: KnockoutObservable<string>;
    valueUpdate?: string;
    noOverflow?: boolean;
    enableMaxWidth?: boolean;
    positionAbove?: boolean;
    attr?: MaybeObservableAttributes;
    preventBubble?: string;
    datepickerOptions?: JQueryUI.DatepickerOptions;
    enable?: MaybeObservable<boolean>;
    disable?: MaybeObservable<boolean>;
    meta?: MaybeObservable<string>;
    text?: MaybeObservable<string>;
    metaPlaceholder?: MaybeObservable<string>;
    placeholder?: MaybeObservable<string>;
    showButton?: boolean;
    buttonFocusable?: boolean;
}

class DatePickerViewModel {
    value: KnockoutObservable<string>;
    valueUpdate: string;
    noOverflow: boolean;
    enableMaxWidth?: boolean;
    positionAbove?: boolean;
    attributes: ObservableAttributes;
    buttonAttributes: Dictionary;
    preventBubble: string;
    datepickerOptions?: JQueryUI.DatepickerOptions;
    enable: KnockoutObservable<boolean>;
    label: KnockoutObservable<string>;
    id: string;
    required: KnockoutObservable<boolean>;
    showButton: boolean;
    ariaLabel: KnockoutComputed<string>;

    constructor(params: IDateParams, input: Element) {
        this.value = params.value;
        this.valueUpdate = params.valueUpdate;
        this.required = createRequiredObservable(params.value);
        this.datepickerOptions = params.datepickerOptions || {};
        this.label = selectLabel(params.meta, params.text);
        this.enable = createEnabler(params.enable, params.disable);
        this.preventBubble = params.preventBubble || "";
        this.showButton = params.showButton === undefined ? true : !!params.showButton;
        this.noOverflow = params.noOverflow ? params.noOverflow : false;
        this.enableMaxWidth = params.enableMaxWidth ? params.enableMaxWidth : false;

        this.positionAbove = params.positionAbove ? params.positionAbove : false;

        this.ariaLabel = ko.pureComputed(() => {
            return this.value()
                ? `${metaCache.translate("FlowHtml.Translations.ChangeDate")}, ${this.value()}`
                : `${metaCache.translate("FlowHtml.Translations.ChooseDate")}`;
        });

        // We can't use attr to set id in this case as it needs to be set before we initialize jQuery UI datepicker.
        // Otherwise will set its own id first that will then be overwritten by attr.
        // This would cause clicking dates in calendar to cause page navigate as link behavior won't be suppressed.
        this.attributes = interceptAttributesWithDefaultsIgnoreId(
            {
                autocomplete: "off",
                placeholder: selectPlaceholder(params.metaPlaceholder, params.placeholder)
            },
            params.attr
        );

        this.buttonAttributes = {
            tabindex: params.buttonFocusable ? null : "-1"
        };

        // We can't allow id to change due to reasons listed above. So we take a snapshot and use that.
        this.id = selectIdStatic("ko_date", params.attr);
        input.setAttribute("id", this.id);
    }
}

ko.components.register("ko-date", {
    viewModel: {
        createViewModel(params: any, componentInfo: KnockoutComponentTypes.ComponentInfo) {
            const input = (componentInfo.element as Element).querySelector("input");
            return new DatePickerViewModel(params, input);
        }
    },
    template: viewHtml,
    synchronous: true
});
